import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Container, Tooltip, Typography } from '@mui/material';
import { useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputField } from '../../../../../components/atoms/input-field';
import { useCreateBillingGroup } from '../../../hooks/mutation/use-create-billing-group';
import { useUpdateBillingGroup } from '../../../hooks/mutation/use-update-billing-group';
import DiscountTemplateCard from '../../atoms/discount-template-card';

import { useCreateBillingGroupValidationSchema } from './schema/create-billing-group-validation-schema';

import { LoadingButton } from '@/components/atoms/loading-button/loading-button';
import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { useDiscountTemplates } from '@/features/hosts/hooks/query/use-discount-templates';
import { useNavigateBack } from '@/hooks/use-navigate-back';
import { usePermissions } from '@/permissions/can';

interface FormValues {
  name: string;
  discountTemplatesIds: number[];
}

const defaultValues: FormValues = {
  name: '',
  discountTemplatesIds: [],
};

export const CreateBillingGroupForm = () => {
  const { t } = useTranslation(['billingGroups', 'common', 'validation']);
  const permissions = usePermissions();

  const { id, discountTemplates, groupName } = useSearch({
    strict: false,
  });
  const isEditing = !!groupName && !!id;
  const cannotUpdateBillingGroupName = isEditing && permissions.cannot('update', 'BillingGroups');

  const { navigateBack } = useNavigateBack();

  const { data: discountsTemplatesData, isLoading, error } = useDiscountTemplates();

  const { mutateAsync: createBillingGroup, isPending: isPendingCreateBillingGroup } =
    useCreateBillingGroup(() => navigateBack('/billing-groups'));

  const { mutateAsync: updateBillingGroup, isPending: isPendingUpdateBillingGroup } =
    useUpdateBillingGroup(() => navigateBack('/billing-groups'));

  const validationSchema = useCreateBillingGroupValidationSchema();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { isValid, isDirty },
  } = useForm({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  const onSubmit = (data: FormValues) => {
    if (groupName && id) {
      updateBillingGroup({
        billingGroupId: id,
        ...data,
        name: cannotUpdateBillingGroupName ? groupName : data.name,
      });
    } else {
      createBillingGroup(data);
    }
  };

  useEffect(() => {
    if (groupName || discountTemplates) {
      reset({
        name: groupName,
        discountTemplatesIds: discountTemplates?.map(({ id }) => id),
      });
    }
  }, [location]);

  return (
    <Container maxWidth="lg" sx={{ flexGrow: 1 }}>
      <Box
        sx={{
          py: 4,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
        }}>
        <Box sx={{ mb: 2, height: '100%' }}>
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <Tooltip
                title={t('common:notSufficientPermissions')}
                disableHoverListener={!cannotUpdateBillingGroupName}
                disableFocusListener={!cannotUpdateBillingGroupName}>
                <span style={{ width: '100%', display: 'block' }}>
                  <InputField
                    {...field}
                    fullWidth
                    label={t('billingGroups:create.name')}
                    name="name"
                    required
                    disabled={cannotUpdateBillingGroupName}
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                </span>
              </Tooltip>
            )}
          />
          {discountsTemplatesData && discountsTemplatesData?.templates?.length > 0 && (
            <Typography color="inherit" sx={{ my: 1 }} variant="body2">
              {t('billingGroups:create.discount')}
            </Typography>
          )}
          <Controller
            control={control}
            name="discountTemplatesIds"
            render={({ field: { value } }) => {
              const handleToggleDiscountTemplateCard = (discountTemplateId: number) => {
                let valueCopy = [...value];

                if (
                  valueCopy.some(
                    selectedDiscountTemplateId => selectedDiscountTemplateId === discountTemplateId,
                  )
                ) {
                  valueCopy = valueCopy.filter(
                    selectedDiscountTemplateId => selectedDiscountTemplateId !== discountTemplateId,
                  );
                } else {
                  valueCopy.push(discountTemplateId);
                }

                setValue('discountTemplatesIds', valueCopy, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              };

              return (
                <Box sx={{ my: 2 }}>
                  {discountsTemplatesData?.templates?.map(({ name, id }) => {
                    return (
                      <DiscountTemplateCard
                        key={id}
                        discountTemplateId={id}
                        isSelected={value.some(
                          selectedDiscountTemplateId => selectedDiscountTemplateId === id,
                        )}
                        discountTemplateName={name}
                        toggleSelection={(_, id) => {
                          handleToggleDiscountTemplateCard(id);
                        }}
                      />
                    );
                  })}
                  <DataStatusIndicator
                    dataLength={discountsTemplatesData?.templates?.length ?? 0}
                    isFetching={isLoading}
                    error={error?.response?.data?.message}
                  />
                </Box>
              );
            }}
          />
        </Box>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            pt: 3,
          }}>
          <Button
            color="primary"
            onClick={() => navigateBack('/billing-groups')}
            variant="text"
            sx={{ mr: 2 }}>
            {t('common:button.cancel')}
          </Button>
          <LoadingButton
            color="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || !isDirty}
            loading={isPendingCreateBillingGroup || isPendingUpdateBillingGroup}
            variant="contained">
            {t('common:button.confirm')}
          </LoadingButton>
        </Box>
      </Box>
    </Container>
  );
};
